import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, toTakkState } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls =
  "h-12 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-base text-[#FDFBF7] placeholder:text-[#A8A29E]/50 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30";

export default function MinKupong() {
  const navigate = useNavigate();
  const [epost, setEpost] = useState("");
  const [telefon, setTelefon] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(epost.trim()) && telefon.replace(/\D/g, "").length >= 8 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await api.post("/lookup", { epost: epost.trim(), telefon: telefon.trim() });
      navigate("/takk", { state: toTakkState(res.data) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="flex items-center justify-center animate-fade-in">
          <Logo />
        </header>

        <main className="mt-10 flex-1 text-center">
          <div className="animate-fade-up">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <Ticket className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl font-600 text-[#FDFBF7] sm:text-5xl">Hent kupongen din</h1>
            <p className="mx-auto mt-4 max-w-md text-base text-[#FDFBF7]/90">
              Skriv inn e-posten og telefonnummeret du registrerte deg med, så finner vi kupongen din.
            </p>
          </div>

          <form onSubmit={submit} noValidate data-testid="lookup-form" className="mx-auto mt-8 flex max-w-sm flex-col gap-4 animate-fade-up rounded-3xl border border-white/10 bg-[#282421]/90 p-6 text-left shadow-2xl sm:p-8" style={{ animationDelay: "0.15s" }}>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-[#FDFBF7]">E-post</span>
              <input type="email" value={epost} onChange={(e) => setEpost(e.target.value)} placeholder="ola@epost.no" data-testid="lookup-email-input" className={inputCls} autoComplete="email" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-[#FDFBF7]">Telefonnummer</span>
              <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="+47 400 00 000" data-testid="lookup-phone-input" className={inputCls} autoComplete="tel" />
            </label>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="lookup-submit-btn"
              className="mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-base font-700 uppercase tracking-wide text-[#1A1715] transition-colors hover:bg-[#E5C564] disabled:cursor-not-allowed disabled:bg-[#D4AF37]/25 disabled:text-[#1A1715]/50"
            >
              {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Henter…</> : "Vis kupongen min"}
            </button>
          </form>
        </main>

        <footer className="mt-8 text-center text-xs text-[#A8A29E]/60">
          <Link to="/gave/registrer" className="hover:text-[#D4AF37]" data-testid="back-to-registration">Ikke registrert ennå? Registrer deg her</Link>
        </footer>
      </div>
    </div>
  );
}
